import { QuestionBase } from './question-base';
import { HttpDataService } from '../../../core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
/*

Sample:

new EntityQuestion({
  key: 'name',
  label: 'nouns.name',
  // value: Entity.field,
  // entity: Entity,
  required: true,
  dataProvider: this.clientsService
  order: 1
}),

*/
export class EntityQuestion extends QuestionBase<string> {
  controlType = 'entity';
  dataProvider: HttpDataService;
  selected: any;
  isLocalEntity: any;
  localEntityData: any;
  entityOptions = {
    preload: false,
    multiple: false,
    filters: null,
    valueField: 'id',
    nameField: 'name',
    listParser: null,
    freeEdit: false,
    dropdown: null
  };
  options: any[] = [];
  entities: any[] = [];
  customEventHandlers: any = {};
  optionLabel: string;

  searchSubject = new Subject<string>();
  optionsSubject = new BehaviorSubject(this.options);

  constructor(options: {} = {}) {
    super({ ...options, designSystem: true });
    this.entityOptions = { ...this.entityOptions, ...options['entityOptions'] };
    this.dataProvider = options['dataProvider'];
    this.title = this.title || 'actions.addTags';
    this.optionLabel = this.optionLabel || 'actions.select';
    this.isLocalEntity = options['isLocalEntity'];
    this.localEntityData = options['localEntityData'];

    if (options['entity']) {
      this.setOptions([options['entity']]);
    }
    this.preload(this.entityOptions.preload);

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => this.search(term))
      )
      .subscribe((data) => this.setOptions(data && data.docs ? data.docs : []));

    this.setEventHandlers();
  }

  getOptions$(): Observable<any> {
    return this.optionsSubject.asObservable();
  }

  setEventHandlers() {
    this.customEventHandlers = { ...this.eventHandlers };
    this.eventHandlers = {
      ...this.eventHandlers,
      onKeyup: (value) => this.onKeyup(value),
      onChange: (value) => this.onChange(value)
    };
  }

  onChange(value) {
    const val = this.parseValue(value);
    if (this.customEventHandlers.onChange) {
      this.customEventHandlers.onChange(val, this);
    }
    if (this.customEventHandlers.onSelected) {
      const entity = this.entities.find((entity) => entity[this.entityOptions.valueField] === val);
      if (entity) {
        this.customEventHandlers.onSelected(entity);
      }
      this.updateChildQuestions(entity);
    }
  }

  onKeyup(term) {
    if (this.customEventHandlers.onKeyup) {
      this.customEventHandlers.onKeyup(term, this);
    }
    this.searchSubject.next(term);
  }

  parseValue(value) {
    if (!this.entityOptions.multiple) {
      return value && value instanceof Array && value.length ? value[0] : value;
    }
    return value;
  }

  preload(preloadValue) {
    if (preloadValue) {
      const id = typeof preloadValue === 'boolean' ? null : { _id: preloadValue };
      const filters = Object.assign({ limit: 100 }, this.entityOptions.filters, id);
      if (!this.isLocalEntity) {
        this.dataProvider
          .search(null, false, filters)
          .subscribe((data) => this.setOptions(data && data.docs ? data.docs : []));
      }
    }
    if (this.isLocalEntity) {
      this.search(preloadValue).subscribe((data) => this.setOptions(data && data.docs ? data.docs : []));
    }
  }

  search(term) {
    const limit = term && term.length > 5 ? 100 : 10;
    const filters = { ...this.entityOptions.filters, limit };
    if (this.isLocalEntity) {
      const docs = this.localEntityData.docs.filter((x) => x.name.toLowerCase().includes(term.toLowerCase()));
      return of({ docs });
    }
    this.entityOptions.dropdown = term && term.length > 2 ? true : null;
    return this.dataProvider.search(encodeURIComponent(term), true, filters);
  }

  setOptions(entities) {
    if (entities && entities.length) {
      this.entities = entities;
      this.options = entities.map((doc) => this.mapEntity(doc));
    } else {
      this.entities = [];
      this.options = [];
    }
    this.optionsSubject.next(this.options);
  }

  mapEntity(entity) {
    const label = this.entityOptions.listParser
      ? this.entityOptions.listParser(entity)
      : entity[this.entityOptions.nameField];
    return {
      value: entity[this.entityOptions.valueField],
      label
    };
  }
}
